"use client";
import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import useForm from "./useForm";
import {
  add_to_useful_comments_mutation,
  create_comment_mutation,
  delete_comment_mutation,
  get_model_comments,
  get_model_comments_with_replies,
  get_user_comments,
  reply_comment_mutation,
} from "@core/services/comments";
import useElementOnScreen from "./useElementOnScreen";
import { array_unique_by_key } from "@core/helpers/array";
import { is_function } from "@core/helpers/function";
import { useAuthContext } from "../contexts/AuthContext";
import { toast_configs } from "@core/constants/toast_configs";
import useTranslationOr from "./useTranslationOr";

const comments_reducer = (state, { type, payload }) => {
  switch (type) {
    case "GET_USER_COMMENTS_PENDING":
      return { ...state, get_comments_loading: true };
    case "GET_USER_COMMENTS_END":
      return { ...state, get_comments_loading: false };
    case "GET_USER_COMMENTS_SUCCESS":
      return { ...state, comments: payload?.data, pagination: payload?.paginate };
    case "GET_MODEL_COMMENTS_PENDING":
      return { ...state, get_comments_loading: true };
    case "GET_MODEL_COMMENTS_END":
      return { ...state, get_comments_loading: false };
    case "GET_MODEL_COMMENTS_SUCCESS":
      return {
        ...state,
        comments: array_unique_by_key(
          [...state.comments, ...(payload?.data || [])],
          "id"
        ),
        pagination: payload?.pagination,
        has_more:
          payload?.pagination?.page === payload?.pagination?.pages
            ? false
            : state.has_more,
      };
    case "RESET_MODEL_COMMENTS":
      return { ...state, comments: [], pagination: { page: 0, pages: 1 }, has_more: true };
    case "CREATE_COMMENT_PENDING":
      return { ...state, create_comment_loading: true };
    case "CREATE_COMMENT_END":
      return { ...state, create_comment_loading: false };
    case "CREATE_COMMENT_SUCCESS":
      return {
        ...state,
        comments: [payload, ...(state.comments || [])],
      };
    case "REPLY_COMMENT_PENDING":
      return { ...state, create_comment_loading: true };
    case "REPLY_COMMENT_END":
      return { ...state, create_comment_loading: false };
    case "REPLY_COMMENT_SUCCESS": {
      if (payload?.is_with_replies) {
        const _comments = [...(state.comments || [])];
        const index = _comments?.findIndex(
          (item) => item?.id === state?.reply_to?.id
        );
        _comments[index] = {
          ..._comments[index],
          replies: [...(_comments[index]?.replies || []), payload?.data],
        };
        return { ...state, comments: _comments };
      } else {
        return {
          ...state,
          comments: [payload?.data, ...(state.comments || [])],
        };
      }
    }
    case "USEFUL_COMMENT_SUCCESS": {
      const _comments = [...(state.comments || [])];
      const index = _comments?.findIndex((item) => item?.id === payload?.data?.id);
      _comments[index] = {
        ..._comments[index],
        ...payload?.data,
      };
      return { ...state, comments: _comments };
    }
    case "DELETE_COMMENT_PENDING":
      return { ...state, delete_comment_loading: { ...state.delete_comment_loading, [payload]: true } };
    case "DELETE_COMMENT_END":
      return { ...state, delete_comment_loading: { ...state.delete_comment_loading, [payload]: false } };
    case "DELETE_COMMENT_SUCCESS":
      return { ...state, comments: (state.comments || []).filter((item) => item?.id !== payload) };
    case "SET_REPLY_TO":
      return { ...state, reply_to: payload };
    case "SET_PAGINATION":
      return { ...state, pagination: payload(state?.pagination) };

    default:
      break;
  }
};


export const comment_types = {
  comment: 1,
  forum: 2,
}

// values accepted by the API as `sort.field` (getComments / Product.comments_paginate / Blog.comments_paginate)
export const comment_sort_fields = {
  newest: "newest",
  oldest: "oldest",
  buyer_first: "buyer_first",
  most_replied: "most_replied",
}

const useComments = (
  model,
  { is_with_replies = false, limit = 15, lazy_load = true, has_pagination = false, comment_type = comment_types.comment, comment_gql, comments_mockup, initial, initial_sort } = {}
) => {
  const { is_auth } = useAuthContext();
  const t_or = useTranslationOr();
  const [comment_form, set_comment_form, on_change_comment_form] = useForm();
  const [state, dispatch] = useReducer(comments_reducer, {
    // chawk-bazar / ciseco: `initial` ({ comments, pagination }) lets a server-fetched first page be rendered (SSR) instead of starting empty.
    comments: initial?.comments || [],
    get_comments_loading: initial?.comments ? false : true,
    create_comment_loading: false,
    delete_comment_loading: {},
    limit,
    pagination: initial?.comments ? (initial?.pagination || { page: 1, pages: 1 }) : { page: 0, pages: 1 },
    has_more: initial?.comments ? !((initial?.pagination?.page ?? 1) >= (initial?.pagination?.pages ?? 1)) : true,
    reply_to: {},
  });

  const element_ref = useRef(null);
  const is_visible = useElementOnScreen(element_ref, state?.comments);

  // Optional sorting: pass `initial_sort` (one of comment_sort_fields) to enable it, then use the returned `sort` / `set_sort`.
  // Without it nothing changes (newest first, as before). A sort change restarts the list from page 1; responses of an older sort are dropped.
  const [sort_field, set_sort] = useState(initial_sort);
  const sort = sort_field ? { field: sort_field } : { operator: "desc" };
  const sort_key = JSON.stringify(sort);
  const sort_key_ref = useRef(sort_key);
  sort_key_ref.current = sort_key;
  const previous_sort_key = useRef(sort_key);

  const get_comments = async (page) => {
    const request_sort_key = sort_key;
    dispatch({ type: "GET_MODEL_COMMENTS_PENDING" });
    let result;
    if (is_with_replies) {
      result = await get_model_comments_with_replies({
        model_id: model?.model_id,
        sort,
        filter: { type: comment_type, reply_to_id: null },
        page,
        limit: state.limit,
      }, { comments_mockup });
    } else {
      result = await get_model_comments({
        model_id: model?.model_id,
        sort,
        filter: { type: comment_type },
        page,
        limit: state.limit,
      }, { comments_mockup });
    }

    if (request_sort_key !== sort_key_ref.current) return;

    if (result.status) {
      const { data, paginate } = result?.data;
      dispatch({
        type: "GET_MODEL_COMMENTS_SUCCESS",
        payload: { data, pagination: paginate },
      });
    }

    dispatch({ type: "GET_MODEL_COMMENTS_END" });
  };

  // `fetch_policy: "network-only"` skips the browser's Apollo cache (e.g. to reload after a delete)
  const _get_user_comments = async (page = 1, { fetch_policy } = {}) => {
    dispatch({ type: "GET_USER_COMMENTS_PENDING" });
    const filter = { type: comment_type };
    if (comment_type === comment_types.forum) {
      filter['reply_to_id'] = null
    }
    const result = await get_user_comments({
      page,
      limit: state.limit,
      filter,
    }, {
      custom_gql: comment_gql,
      ...(fetch_policy ? { fetchPolicy: fetch_policy } : {}),
    });
    if (result.status) {
      dispatch({
        type: "GET_USER_COMMENTS_SUCCESS",
        payload: result?.data,
      });
    }
    dispatch({ type: "GET_USER_COMMENTS_END" });
  };

  useEffect(() => {
    if (model?.model_id) {
      if (lazy_load) {
        const next_page = state?.pagination?.page + 1;
        if (next_page <= state?.pagination?.pages && is_visible) {
          get_comments(next_page);
        }
      }
    } else {
      if (!has_pagination)
        _get_user_comments();
    }
  }, [model?.model_id, is_visible, state?.pagination]);

  useEffect(() => {
    if (!lazy_load && model && !initial?.comments) {
      get_comments();
    }
    // else if (has_pagination) {
    //   if (!model) {
    //     _get_user_comments();
    //   }
    // }
    set_comment_form((prev) => ({ ...(prev || {}), rate: 5 }));
  }, []);

  useEffect(() => {
    if (previous_sort_key.current === sort_key) return;
    previous_sort_key.current = sort_key;
    if (!model?.model_id) return;
    dispatch({ type: "RESET_MODEL_COMMENTS" });
    if (!lazy_load) get_comments();
  }, [sort_key]);

  const load_more_comments_handler = useCallback(
    (e) => {
      e.preventDefault();
      const next_page = state?.pagination?.page + 1;
      if (next_page <= state?.pagination?.pages) {
        if (model) {
          get_comments(next_page);
        } else {
          _get_user_comments(next_page)
        }
      }
    },
    [state.pagination]
  );

  const create_comment_handler = useCallback(
    async (e, args = {}) => {
      e.preventDefault();
      if (is_auth) {
        let valid = true;
        if ((comment_form?.text || "")?.length === 0) {
          valid = false;
          set_comment_form((prev) => ({
            ...prev,
            errors: { text: "validations.text_required" },
          }));
        }
        const { success_callback, error_callback } = args;
        if (valid) {
          dispatch({ type: "CREATE_COMMENT_PENDING" });
          const result = await create_comment_mutation({
            input: {
              title: comment_form?.title,
              text: comment_form?.text,
              rate: comment_type === comment_types.comment ?
                parseInt(comment_form?.rate) > 0 ?
                  parseInt(comment_form?.rate) :
                  5 :
                null,
              type: comment_type,
              model_id: model?.model_id,
              model_type: model?.model_type,
              model_name: model?.model_name,
            },
          });
          if (result?.status) {
            dispatch({ type: "CREATE_COMMENT_SUCCESS", payload: result?.data });
            set_comment_form({ rate: 5 });
            if (is_function(success_callback)) {
              success_callback(result?.data);
            }
          } else {
            if (is_function(error_callback)) {
              error_callback(result?.errors);
            }
          }
          dispatch({ type: "CREATE_COMMENT_END" });
        }
      } else {
        const { not_auth_callback } = args;

        if (is_function(not_auth_callback)) {
          not_auth_callback();
        } else {
          try {
            toast_configs(t_or("validations.login_first", "لطفا ابتدا وارد شوید."), "info");
          } catch (e) {
            console.error("toast_configs function in useComments error", e);
          }
        }
      }
    },
    [comment_form, is_auth]
  );

  const reply_comment_handler = useCallback(
    async (e, args = {}) => {
      e.preventDefault();
      if (is_auth) {
        let valid = true;
        if ((comment_form?.text || "")?.length === 0) {
          valid = false;
          set_comment_form((prev) => ({
            ...prev,
            errors: { text: "validations.text_required" },
          }));
        }
        const { success_callback, error_callback } = args;
        if (valid) {
          dispatch({ type: "REPLY_COMMENT_PENDING" });
          const result = await reply_comment_mutation({
            input: {
              title: comment_form?.title,
              text: comment_form?.text,
              reply_id: state?.reply_to?.id,
            },
          });
          if (result?.status) {
            dispatch({
              type: "REPLY_COMMENT_SUCCESS",
              payload: { data: result?.data, is_with_replies },
            });
            set_comment_form({ rate: 5 });
            set_reply_to(null);

            if (is_function(success_callback)) {
              success_callback(result?.data);
            }
          } else {
            if (is_function(error_callback)) {
              error_callback(result?.errors);
            }
          }
          dispatch({ type: "REPLY_COMMENT_END" });
        }
      } else {
        const { not_auth_callback } = args;
        if (is_function(not_auth_callback)) {
          not_auth_callback();
        } else {
          try {
            toast_configs(t_or("validations.login_first", "لطفا ابتدا وارد شوید."), "info");
          } catch (e) {
            console.error("toast_configs function in useComments error", e);
          }
        }
      }
    },
    [comment_form, model, state?.reply_to]
  );

  const add_to_useful_comments_handler = useCallback(
    async (e, args = {}) => {
      e.preventDefault();
      if (is_auth) {
        const { success_callback, error_callback, comment, useful_type = "like" } = args;
        let useful;
        let useful_count;
        if (useful_type === "like") {
          useful = comment?.user_useful < 1 ? 1 : 0;
          useful_count = comment?.user_useful === -1
            ? { down: (comment?.useful_count?.down - 1), up: (comment?.useful_count?.up + 1) } :
            comment?.user_useful === 1 ?
              { ...comment?.useful_count, up: (comment?.useful_count?.up - 1) }
              : { ...comment?.useful_count, up: (comment?.useful_count?.up + 1) }
        } else {
          useful = comment?.user_useful > -1 ? -1 : 0;
          useful_count = comment?.user_useful === 1
            ? { down: (comment?.useful_count?.down + 1), up: (comment?.useful_count?.up - 1) } :
            comment?.user_useful === -1 ?
              { ...comment?.useful_count, down: (comment?.useful_count?.down - 1) }
              : { ...comment?.useful_count, down: (comment?.useful_count?.down + 1) }
        }

        const result = await add_to_useful_comments_mutation({
          comment_id: comment?.id,
          useful,
        });
        if (result?.status) {
          dispatch({
            type: "USEFUL_COMMENT_SUCCESS",
            payload: {
              data: {
                id: comment?.id,
                user_useful: useful,
                useful_count,
              },
            },
          });

          if (is_function(success_callback)) {
            success_callback(result?.data);
          }
        } else {
          if (is_function(error_callback)) {
            error_callback(result?.errors);
          }
        }
      } else {
        const { not_auth_callback } = args;
        if (is_function(not_auth_callback)) {
          not_auth_callback();
        } else {
          try {
            toast_configs(t_or("validations.login_first", "لطفا ابتدا وارد شوید."), "info");
          } catch (e) {
            console.error("toast_configs function in useComments error", e);
          }
        }
      }
    },
    [comment_form, model, state?.reply_to]
  );

  // The user deletes their own comment, question or reply (soft delete); the API refuses a confirmed one (403 not_allowed).
  // On the user's list (no model) the current page is loaded again after the delete (the previous page when the
  // deleted comment was the last one of the page); on a model's list the comment is just removed.
  const delete_comment_handler = useCallback(
    async (e, args = {}) => {
      e?.preventDefault?.();
      const { comment, success_callback, error_callback } = args;
      if (!comment?.id) return;
      dispatch({ type: "DELETE_COMMENT_PENDING", payload: comment.id });
      const result = await delete_comment_mutation({ id: comment.id });
      if (result?.status) {
        if (is_function(success_callback)) {
          success_callback(result?.data);
        }
        if (model) {
          dispatch({ type: "DELETE_COMMENT_SUCCESS", payload: comment.id });
        } else {
          const page = state?.pagination?.page || 1;
          await _get_user_comments(state?.comments?.length <= 1 && page > 1 ? page - 1 : page, { fetch_policy: "network-only" });
        }
      } else if (is_function(error_callback)) {
        error_callback(result?.errors);
      }
      dispatch({ type: "DELETE_COMMENT_END", payload: comment.id });
    },
    [model, state?.comments, state?.pagination]
  );

  const set_reply_to = useCallback((comment) => {
    dispatch({ type: "SET_REPLY_TO", payload: comment });
    set_comment_form({ rate: 5 });
    if (comment?.id) {
      setTimeout(() => {
        const element = document.getElementById("comment-tab");
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    }
  }, []);

  return {
    comment_form,
    set_comment_form,
    on_change_comment_form,
    state,
    element_ref,
    create_comment_handler,
    reply_comment_handler,
    set_reply_to,
    load_more_comments_handler,
    add_to_useful_comments_handler,
    delete_comment_handler,
    sort: sort_field,
    set_sort,
    _get_user_comments,
    set_pagination: (value) => dispatch({ type: "SET_PAGINATION", payload: value })
  };
};

export default useComments;
